// src/services/auth/TokenAutoExtend.js
import apiClient from "@/shared/api/apiClient";
import { tokenStorage } from "./tokenStorage";

const IS_TEST_MODE = false;

const AUTO_EXTEND_MARGIN_MS = IS_TEST_MODE ? 60 * 1000 : 2 * 60 * 1000;

// Если пользователь неактивен больше 30 минут — не продлеваем
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;

// Максимальное время удержания сессии при несохранённых изменениях
const MAX_KEEP_ALIVE_MS = 60 * 60 * 1000;

// Минимальная задержка, чтобы не было бесконечного цикла
const MIN_DELAY_MS = 5 * 1000;

// ============================================

class TokenAutoExtend {
  timerId = null;
  running = false;
  lastActivity = Date.now();
  keepAliveHolders = new Set();
  keepAliveSince = null;

  activityHandler = () => {
    this.lastActivity = Date.now();
  };

  start() {
    if (this.running) return;

    if (!tokenStorage.accessToken) return;

    this.running = true;
    this.lastActivity = Date.now();
    this.attach();

    this.schedule();
  }

  stop() {
    this.running = false;
    clearTimeout(this.timerId);
    this.timerId = null;
    this.detach();
  }

  schedule() {
    if (!this.running) return;

    let delay = tokenStorage.ttlExpiresAt - Date.now() - AUTO_EXTEND_MARGIN_MS;

    if (delay < MIN_DELAY_MS) delay = MIN_DELAY_MS;

    clearTimeout(this.timerId);
    this.timerId = setTimeout(() => this.extend(), delay);
  }

  async extend() {
    if (!this.running) return;

    if (
      !this.hasKeepAlive &&
      Date.now() - this.lastActivity > INACTIVITY_LIMIT_MS
    ) {
      return this.stop();
    }

    try {
      const { data } = await apiClient.get("/login/check-session/", {
        metaData: { isNotificationDisabled: true },
      });

      if (!data?.payload?.result) {
        this.stop();
      }

      const ttl = data?.payload?.ttl_minutes;

      if (!ttl) {
        throw new Error("Сервер не вернул ttl_minutes");
      }

      tokenStorage.updateTTL(ttl);

      this.schedule();
    } catch (e) {
      console.error("[TAE] ❌ Ошибка при продлении токена", e);

      this.stop();
    }
  }

  attach() {
    ["click", "keydown", "mousemove", "scroll"].forEach((e) =>
      window.addEventListener(e, this.activityHandler)
    );
  }

  detach() {
    ["click", "keydown", "mousemove", "scroll"].forEach((e) =>
      window.removeEventListener(e, this.activityHandler)
    );
  }

  holdKeepAlive(id) {
    if (this.keepAliveHolders.size === 0) {
      this.keepAliveSince = Date.now();
    }
    this.keepAliveHolders.add(id);
  }

  releaseKeepAlive(id) {
    this.keepAliveHolders.delete(id);
    if (this.keepAliveHolders.size === 0) {
      this.keepAliveSince = null;
    }
  }

  get hasKeepAlive() {
    if (this.keepAliveHolders.size === 0) return false;
    if (this.keepAliveSince === null) return false;
    return Date.now() - this.keepAliveSince < MAX_KEEP_ALIVE_MS;
  }
}

export default new TokenAutoExtend();
